/**
 * Character market page — the "shop front" for a single character.
 *
 *   1. Character introduction (name, description, tags, 3D preview, greeting)
 *   2. Member-exclusive functions (app-market grid)
 *   3. Entry to the chat experience (/c/:id/chat)
 *
 * Layout is game-like rather than long-scroll: model on one side,
 * function grid on the other. Before acquisition the model is hidden
 * behind a mask carrying the description + acquire button.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EnergyBattery } from '../components/EnergyBattery';
import {
  acquireCharacter,
  checkAccess,
  getCharacterIncident,
  getCharacterMembership,
  getFanCharacter,
  getPublicInfo,
  listCharacterGames,
  launchGame,
  listMyOwnedCharacters,
  type AccessCheckResult,
  type CharacterGameTile,
  type FanCharacterCard,
  type FanCharacterIncident,
  type FanCharacterMembership,
  type PublicCharacterInfo,
} from '../api/fan-api';
import {
  fanCognitoConfigured,
  getStoredFanClaims,
} from '../auth/fan-auth';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { notifyWalletChanged } from '../lib/wallet-bus';
import { Character3DStage } from '../components/Character3DStage';
import { OwnedObjectsPanel } from '../components/OwnedObjectsPanel';

// Hero view modes for owned fans. Default is the operator's static
// banner / thumbnail because the 3D reveal on the chat page is what
// drives acquisition. Once a fan owns the skin they can flip the
// market hero to live 3D and pin that preference per-character —
// "they own it, they get to choose how they see it" (operator's
// explicit ask).
//
// Persistence: localStorage `nx-fan:viewMode:<characterId>` = 'banner' | '3d'.
// Per-character so flipping one to 3D doesn't change the others.
// Unowned fans always see the banner regardless of stored value
// (their localStorage may carry a stale '3d' flag if they once
// owned it but the skin was revoked).
//
// Note: there is NO model-file download in this platform.
// "下載模型" terminology used in earlier UI rounds meant "acquire
// the right to view the skin", not "fetch a GLB". Acquisition
// happens via the mask overlay's 免費加入 / 解鎖 button.
type ViewMode = 'banner' | '3d';

type Phase =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'delisted' }
  | { kind: 'access-denied'; reason: string; needsLogin: boolean }
  | { kind: 'ready'; character: FanCharacterCard; access: AccessCheckResult };

export function CharacterMarket() {
  const { characterId } = useParams<{ characterId: string }>();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [owned, setOwned] = useState<boolean | null>(null);
  const [acquiring, setAcquiring] = useState(false);
  const [acquireErr, setAcquireErr] = useState<string | null>(null);
  // Insufficient-funds prompt — when acquireCharacter returns 402,
  // we surface a modal asking whether to head to /wallet (and from
  // there to topup once ECPay ships) instead of dumping the raw
  // error JSON on the user. Holds the shortfall amounts so the
  // copy can be specific ("還差 N 點").
  const [insufficientFunds, setInsufficientFunds] = useState<{
    required: number;
    current: number;
  } | null>(null);
  const [publicInfo, setPublicInfo] = useState<PublicCharacterInfo | null>(null);
  // Per `character-incident-monitor` — null = no incident or still
  // probing; truthy = active open incident, swap banner to
  // maintenance image + lock the 開始聊天 CTA.
  const [incident, setIncident] = useState<FanCharacterIncident | null>(null);
  // Per `fan-youtube-integration` — cached membership lookup. Fetched
  // once on mount; the endpoint NEVER calls YouTube live so this is
  // a cheap DDB GetItem. `null` while loading; the union shape after.
  const [membership, setMembership] = useState<FanCharacterMembership | null>(null);
  useEffect(() => {
    if (!characterId) return;
    let alive = true;
    getCharacterMembership(characterId)
      .then((r) => { if (alive) setMembership(r); })
      .catch(() => { /* network blip — leave null, no badge shows */ });
    return () => { alive = false; };
  }, [characterId]);
  // Current banner index for the gallery carousel. Reset to 0 every
  // time the character changes. Only meaningful when banner mode is
  // active and the character has >0 published gallery images.
  const [galleryIdx, setGalleryIdx] = useState(0);
  useEffect(() => { setGalleryIdx(0); }, [characterId]);

  // Per `fan-game-bridge` — games whose scope covers this character
  // (synced from the 後台遊戲註冊表; one FeatureTile per game).
  const [games, setGames] = useState<CharacterGameTile[]>([]);
  const [launchingGameId, setLaunchingGameId] = useState<string | null>(null);
  useEffect(() => {
    if (!characterId) return;
    let alive = true;
    listCharacterGames(characterId)
      .then((items) => { if (alive) setGames(items); })
      .catch(() => { /* endpoint absent / logged-out — no game tiles */ });
    return () => { alive = false; };
  }, [characterId]);
  const onLaunchGame = async (gameId: string): Promise<void> => {
    if (!characterId) return;
    setLaunchingGameId(gameId);
    try {
      const r = await launchGame(gameId, characterId);
      // Single-use 5-min token rides the URL; the game strips it from
      // history after exchange (per its interface spec).
      window.open(r.launchUrl, '_blank', 'noopener');
    } catch (e) {
      alert((e as Error).message || '啟動失敗,請稍後再試');
    } finally {
      setLaunchingGameId(null);
    }
  };

  // Probe incident status on mount + poll every 60s while the page is
  // open. Long-poll keeps the maintenance state in sync if the
  // operator clicks 標為已處理 mid-session.
  useEffect(() => {
    if (!characterId) return;
    let alive = true;
    let timer: number | undefined;
    const tick = async (): Promise<void> => {
      try {
        const inc = await getCharacterIncident(characterId);
        if (alive) setIncident(inc);
      } catch { /* network blip — ignore */ }
      if (alive) timer = window.setTimeout(() => { void tick(); }, 60_000);
    };
    void tick();
    return () => { alive = false; if (timer) window.clearTimeout(timer); };
  }, [characterId]);
  // Lightbox modal — opens when the user clicks the banner or the
  // magnifier icon. Holds the URL of the image to display at full
  // resolution; null when closed.
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!lightboxUrl) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setLightboxUrl(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxUrl]);

  // Per-character view-mode preference, persisted to localStorage.
  // Default 'banner' so first-time visitors get the operator's
  // designed hero, not a heavy three.js mount on page load.
  const viewModeKey = characterId ? `nx-fan:viewMode:${characterId}` : '';
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined' || !viewModeKey) return 'banner';
    return window.localStorage.getItem(viewModeKey) === '3d' ? '3d' : 'banner';
  });
  const persistViewMode = useCallback((next: ViewMode) => {
    setViewMode(next);
    if (viewModeKey) {
      try { window.localStorage.setItem(viewModeKey, next); } catch { /* private mode */ }
    }
  }, [viewModeKey]);

  const signedIn = Boolean(getStoredFanClaims()?.sub);

  useDocumentTitle(phase.kind === 'ready' ? phase.character.name : '角色');

  // Owned-list refetch as a callable — used on mount and after every
  // successful acquire so the mask peels away immediately.
  const refreshOwned = useCallback(async (id: string) => {
    if (!signedIn) { setOwned(false); return; }
    try {
      const r = await listMyOwnedCharacters();
      setOwned(r.items.some((it) => it.characterId === id));
    } catch {
      setOwned(false);
    }
  }, [signedIn]);

  useEffect(() => {
    if (!characterId) return;
    let alive = true;
    (async () => {
      try {
        // character-delisting — a delisted character returns 410
        // CHARACTER_DELISTED. Surface a dedicated friendly state instead
        // of the generic not-found.
        let delisted = false;
        const [card, access] = await Promise.all([
          getFanCharacter(characterId).catch((e: Error & { code?: string; status?: number }) => {
            if (e.code === 'CHARACTER_DELISTED' || e.status === 410) delisted = true;
            return null;
          }),
          checkAccess(characterId).catch(() => ({ allowed: false, reason: 'UNKNOWN' } as AccessCheckResult)),
        ]);
        if (!alive) return;
        if (delisted) { setPhase({ kind: 'delisted' }); return; }
        if (!card) { setPhase({ kind: 'not-found' }); return; }
        if (!access.allowed && access.reason !== 'NEEDS_ENROLLMENT') {
          const needsLogin = access.reason === 'NOT_LOGGED_IN';
          setPhase({ kind: 'access-denied', reason: access.reason, needsLogin });
          return;
        }
        setPhase({ kind: 'ready', character: card, access });
      } catch (e) {
        if (!alive) return;
        setPhase({ kind: 'access-denied', reason: (e as Error).message, needsLogin: false });
      }
    })();
    void refreshOwned(characterId);
    return () => { alive = false; };
  }, [characterId, refreshOwned]);

  // Lazy-load public-info only when we actually need it for the 3D
  // viewport — i.e. owned + viewMode='3d'. Saves a network round-
  // trip on first-paint for banner-mode visitors (which is the
  // majority of traffic).
  useEffect(() => {
    if (!characterId) return;
    const current = phase.kind === 'ready' ? phase.character : null;
    const needsModelFallback =
      current?.type === 'model3d' &&
      !current.thumbnailUrl &&
      (!current.gallery || current.gallery.length === 0);
    if ((owned !== true || viewMode !== '3d') && !needsModelFallback) return;
    if (publicInfo) return;
    let alive = true;
    void getPublicInfo(characterId)
      .then((p) => { if (alive) setPublicInfo(p); })
      .catch(() => { /* ignore — fall back to banner UI */ });
    return () => { alive = false; };
  }, [characterId, owned, viewMode, publicInfo, phase]);

  const doAcquire = useCallback(async () => {
    if (!characterId) return;
    setAcquiring(true);
    setAcquireErr(null);
    try {
      await acquireCharacter(characterId);
      await refreshOwned(characterId);
      // A paid acquire debits the platform wallet — push the header
      // pill so it doesn't lag behind the actual balance for 60s.
      notifyWalletChanged();
    } catch (e) {
      // 402 INSUFFICIENT_FUNDS → divert to the topup-prompt modal
      // instead of dumping the raw JSON message in the page. The
      // request() helper attaches the HTTP status onto the Error
      // object; the body string contains the JSON payload from the
      // chat handler with currentBalance + requiredAmount.
      const err = e as Error & { status?: number };
      const isInsufficient = err.status === 402 && /INSUFFICIENT_FUNDS/.test(err.message);
      if (isInsufficient) {
        // Try to parse the embedded JSON body for the exact numbers.
        // The chat handler's ApiError serializes details into the
        // body; the request() helper concatenates the body into
        // err.message. Falls back to 0/0 if parsing fails.
        let required = 0;
        let current = 0;
        const match = err.message.match(/\{.*\}/);
        if (match) {
          try {
            const parsed = JSON.parse(match[0]) as { error?: { details?: { currentBalance?: number; requiredAmount?: number } } };
            const d = parsed.error?.details;
            if (typeof d?.currentBalance === 'number') current = d.currentBalance;
            if (typeof d?.requiredAmount === 'number') required = d.requiredAmount;
          } catch { /* fall back to defaults */ }
        }
        setInsufficientFunds({ required, current });
      } else {
        setAcquireErr(err.message);
      }
    } finally {
      setAcquiring(false);
    }
  }, [characterId, refreshOwned]);

  if (phase.kind === 'loading') {
    return (
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <div className="rounded-2xl border border-slate-800 nx-skeleton-shimmer h-[60vh]" aria-hidden="true" />
      </div>
    );
  }
  if (phase.kind === 'not-found') {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div
          className="mx-auto mb-3 text-5xl text-slate-600"
          aria-hidden="true"
          style={{ filter: 'drop-shadow(0 0 16px rgba(124,70,243,0.25))' }}
        >？</div>
        <h1 className="text-xl font-semibold mb-2">找不到這個角色</h1>
        <p className="text-slate-400 text-sm mb-6">這個角色可能已下架。</p>
        <Link
          to="/"
          className="inline-block rounded-full bg-violet-500 px-4 py-1.5 text-white text-sm hover:bg-violet-400 active:scale-[0.97] transition shadow-[0_8px_20px_-8px_rgba(124,70,243,0.6)]"
        >回首頁</Link>
      </div>
    );
  }
  if (phase.kind === 'delisted') {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div
          className="mx-auto mb-3 text-5xl text-amber-500/70"
          aria-hidden="true"
          style={{ filter: 'drop-shadow(0 0 16px rgba(245,158,11,0.25))' }}
        >🌙</div>
        <h1 className="text-xl font-semibold mb-2">此角色已下架</h1>
        <p className="text-slate-400 text-sm mb-6">這個角色目前暫停服務,所有聊天與互動功能已停用。若你曾儲值,點數會保留,角色重新上架後即可繼續使用。</p>
        <Link
          to="/"
          className="inline-block rounded-full bg-violet-500 px-4 py-1.5 text-white text-sm hover:bg-violet-400 active:scale-[0.97] transition shadow-[0_8px_20px_-8px_rgba(124,70,243,0.6)]"
        >回首頁</Link>
      </div>
    );
  }
  if (phase.kind === 'access-denied') {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div
          className="mx-auto mb-3 text-5xl"
          aria-hidden="true"
          style={{ filter: 'drop-shadow(0 0 16px rgba(124,70,243,0.3))' }}
        >🔒</div>
        <h1 className="text-xl font-semibold mb-2">{phase.needsLogin ? '需要登入' : '無法存取'}</h1>
        <p className="text-slate-400 text-sm mb-6">
          {phase.needsLogin ? '想看這個角色請先登入或註冊。' : '你不在這個角色的開放名單裡。'}
        </p>
        {phase.needsLogin && fanCognitoConfigured() && (
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="rounded-full bg-violet-500 px-4 py-1.5 text-white text-sm hover:bg-violet-400 active:scale-[0.97] transition shadow-[0_8px_20px_-8px_rgba(124,70,243,0.6)]"
          >登入 / 註冊</button>
        )}
      </div>
    );
  }

  const { character } = phase;
  const comingSoon = character.modelDistribution?.mode === 'coming-soon';
  const canEnterChat = owned === true;
  // Show the acquire mask only when we've definitively confirmed the
  // fan does NOT own the skin (owned===false). While owned===null we
  // leave the mask off to avoid the flash where it briefly shows and
  // then peels away once the ownership check resolves.
  //
  // The character is acquirable when the API returned a card at all
  // (server-side already filtered out non-fanMode characters). Missing
  // `modelDistribution` is treated as `{enabled:true, mode:'free'}` —
  // matches the fan-acquire backend's default. Only explicit
  // `modelDistribution.enabled === false` hides the mask.
  const distEnabled = character.modelDistribution?.enabled !== false;
  const showAcquireMask = owned === false && distEnabled && !comingSoon;
  const needsModelFallback =
    character.type === 'model3d' &&
    !character.thumbnailUrl &&
    (!character.gallery || character.gallery.length === 0);
  const hasStaticVisual =
    !!character.thumbnailUrl ||
    !!character.maintenanceImageUrl ||
    !!(character.gallery && character.gallery.length > 0);
  const canSwitchViewMode = canEnterChat && !incident && hasStaticVisual;
  // Owned + opted-in to 3D + we have public-info loaded with a real
  // model path → mount the live 3D viewport. Otherwise stay on the
  // static banner.
  const show3D = ((owned === true && viewMode === '3d') || needsModelFallback)
    && publicInfo?.type === 'model3d'
    && !!publicInfo.model3d?.modelPath;
  // 電池鐵則:電池在最上面、角色在電池「下方」、兩者不得重疊 —
  // 電池顯示時 section 加 padding-top 保留它的專屬列,角色視圖
  // (h-full 依 content box 計算)整體下移;HUD 角標也一併下移。
  const showBattery = owned === true && !!characterId && !incident;

  return (
    // Game-like fixed layout: model on the left (or top on phones),
    // sidebar of info + function tiles on the right (or below on
    // phones). No long vertical scroll — everything fits in viewport.
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-4 lg:h-[calc(100dvh-4rem)]">
      {/* ── Character hero ──────────────────────────────────────────
          Two view modes — banner (default) and 3D (owned fans
          opt-in). Choice persists per-character via localStorage so
          a fan who flips to 3D once gets 3D by default on every
          subsequent visit. Unowned visitors only ever see banner;
          their toggle is hidden. */}
      <section className={`relative rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 overflow-hidden aspect-[16/10] sm:aspect-[16/9] lg:aspect-auto lg:h-full min-h-[240px] ${showBattery ? 'pt-14' : ''}`}>
        {/* Per `character-energy-system` — show the per-character battery
            here too (read-only: viewing the market must NOT drain it). */}
        {showBattery && (
          <EnergyBattery characterId={characterId} readOnly />
        )}
        {incident ? (
          // Maintenance state — operator's upstream is broken. Swap
          // the banner to the maintenance image (or thumbnail/text
          // fallback) and overlay a 維護中 ribbon. All other banner
          // overlays (3D toggle, carousel arrows) are hidden below
          // via the same `incident` check.
          character.maintenanceImageUrl ? (
            <img src={character.maintenanceImageUrl} alt={character.name} className="w-full h-full object-contain" />
          ) : character.thumbnailUrl ? (
            <img src={character.thumbnailUrl} alt={character.name} className="w-full h-full object-cover opacity-60" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-8xl font-light text-slate-700/60">{character.name.slice(0, 1)}</div>
          )
        ) : show3D && publicInfo ? (
          <Character3DStage info={publicInfo} interactive framing="hero-cover" className="w-full h-full" />
        ) : character.gallery && character.gallery.length > 0 ? (
          // Operator-uploaded banner / promo carousel. Server already
          // filtered to status='published' and sorted by sortOrder.
          (() => {
            const imgs = character.gallery;
            const idx = ((galleryIdx % imgs.length) + imgs.length) % imgs.length;
            const cur = imgs[idx]!;
            return (
              <>
                {/* `object-contain` preserves the operator's full image
                    without cropping — we don't dictate aspect ratio on
                    uploads, so letterbox/pillarbox is acceptable and
                    expected. Click anywhere on the image to open the
                    full-resolution lightbox. */}
                <button
                  type="button"
                  onClick={() => setLightboxUrl(cur.url)}
                  className="absolute inset-0 flex items-center justify-center cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                  aria-label="查看原圖"
                  title="查看原圖"
                >
                  <img
                    src={cur.url}
                    alt={cur.caption ?? character.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </button>
                {cur.caption && (
                  <div className="absolute inset-x-0 bottom-12 flex items-center justify-center pointer-events-none">
                    <div className="rounded-full bg-slate-950/80 px-3 py-1 text-xs text-slate-100 backdrop-blur max-w-[80%] truncate">
                      {cur.caption}
                    </div>
                  </div>
                )}
                {/* Magnifier — top-right when the 3D toggle isn't there
                    (unowned fans), bottom-right alongside the dot row
                    when the 3D toggle takes the top-right slot. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLightboxUrl(cur.url); }}
                  className={`absolute z-10 rounded-full bg-slate-950/70 px-2.5 py-1.5 text-slate-100 backdrop-blur shadow-lg hover:bg-slate-900/80 focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                    canEnterChat ? 'bottom-3 right-3' : 'top-3 right-3'
                  }`}
                  aria-label="查看原圖"
                  title="查看原圖"
                >
                  🔍
                </button>
                {imgs.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setGalleryIdx((i) => i - 1); }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-slate-950/70 px-3 py-2 text-slate-100 backdrop-blur hover:bg-slate-900/80 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      aria-label="上一張"
                    >‹</button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setGalleryIdx((i) => i + 1); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-slate-950/70 px-3 py-2 text-slate-100 backdrop-blur hover:bg-slate-900/80 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      aria-label="下一張"
                    >›</button>
                    {/* Dot indicators */}
                    <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1 pointer-events-none">
                      {imgs.map((_, i) => (
                        <span
                          key={i}
                          className={`h-1.5 w-1.5 rounded-full transition ${
                            i === idx ? 'bg-white' : 'bg-white/30'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            );
          })()
        ) : character.thumbnailUrl ? (
          <img src={character.thumbnailUrl} alt={character.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-8xl font-light text-slate-700/60">{character.name.slice(0, 1)}</div>
        )}

        {/* Maintenance overlay — full-cover ribbon when incident is
            active. Sits above the banner but below other absolute
            chips so the operator's image stays visible. */}
        {incident && (
          <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none bg-slate-950/40 backdrop-blur-[2px]">
            <div className="pointer-events-auto rounded-2xl border border-amber-400/40 bg-slate-950/85 px-6 py-5 text-center max-w-xs shadow-2xl">
              <div className="text-4xl mb-2">🛠</div>
              <div className="text-base font-semibold text-amber-200">維護中</div>
              <div className="mt-1 text-xs text-slate-300 leading-relaxed">
                角色語音 / 對話服務目前正在維護，我們已收到通知並處理中。
              </div>
              <div className="mt-2 text-[10px] text-slate-500 font-mono">
                {new Date(incident.firstSeenAt).toLocaleString()}
              </div>
            </div>
          </div>
        )}

        {/* Game-HUD-style top-left name chip — character name with the
            operator company displayed as small subtitle. */}
        <div className={`absolute left-3 pointer-events-none z-10 space-y-1 ${showBattery ? 'top-16' : 'top-3'}`}>
          <div className="rounded-full bg-slate-950/70 backdrop-blur px-3 py-1 text-sm text-slate-100 font-medium">
            {character.name}
            {character.operatorDisplayName && (
              <span className="ml-2 text-[10px] text-slate-400 font-normal">by {character.operatorDisplayName}</span>
            )}
            {owned && <span className="ml-2 text-blue-400 text-xs">· 已擁有</span>}
          </div>
          {comingSoon && (
            <div className="w-fit rounded-full border border-violet-300/30 bg-violet-500/90 px-3 py-1 text-xs font-semibold text-white shadow-lg backdrop-blur">
              即將上架
            </div>
          )}
          {/* Per `fan-youtube-integration` — member badge / loading hint.
              Only renders for fan IS a member OR the channel's cache is
              pending (so they see "資料載入中" instead of nothing). */}
          {membership?.isMember && (
            <div className="rounded-full bg-rose-600/80 backdrop-blur px-3 py-0.5 text-[11px] text-white font-medium">
              ❤ 會員 · {membership.levelDisplayName}
            </div>
          )}
          {membership && !membership.isMember && membership.reason === 'cache-pending' && (
            <div className="rounded-full bg-slate-950/70 backdrop-blur px-3 py-0.5 text-[10px] text-slate-400">
              會員資料載入中…
            </div>
          )}
        </div>

        {/* Top-right view-mode toggle — visible only when owned AND no incident. */}
        {canSwitchViewMode && (
          <div className={`absolute right-3 z-10 ${showBattery ? 'top-16' : 'top-3'}`}>
            <button
              type="button"
              onClick={() => persistViewMode(viewMode === '3d' ? 'banner' : '3d')}
              aria-label={viewMode === '3d' ? '切換成靜態圖片' : '切換成 3D 互動'}
              title={viewMode === '3d' ? '切換成靜態圖片' : '切換成 3D 互動'}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-3 py-1.5 text-xs text-slate-100 backdrop-blur shadow-lg hover:bg-slate-900/80 focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              {viewMode === '3d' ? (
                <>🖼️ <span>切回 Banner</span></>
              ) : (
                <>🎮 <span>切到 3D</span></>
              )}
            </button>
          </div>
        )}

        {/* Bottom hint chip. Different copy per state:
            - owned + banner mode → "想看 3D? 右上角可切換"
            - owned + 3d mode → "點下方「開始聊天」進入完整互動"
            - unowned: nothing (the mask carries the info) */}
        {canEnterChat && hasStaticVisual && (
          <div className="absolute inset-x-0 bottom-3 flex items-center justify-center pointer-events-none">
            <div className="rounded-full bg-slate-950/80 px-3 py-1 text-xs text-slate-300 backdrop-blur">
              {viewMode === '3d'
                ? '點下方「開始聊天」進入完整互動'
                : '想看 3D? 點右上角切換'}
            </div>
          </div>
        )}

        {/* Acquire CTA — operator-uploaded gallery images are
            free public promo material, so we DON'T blur/dim them.
            The card sits at the bottom edge with just enough
            backdrop for the text to stay readable. Top portion of
            the image stays fully visible. */}
        {showAcquireMask && (
          <div className="absolute inset-x-0 bottom-0 z-[5] pointer-events-none flex flex-col justify-end p-3 sm:p-4">
            <div className="pointer-events-auto rounded-2xl border border-white/10 bg-slate-950/75 backdrop-blur-md p-4 sm:p-5 space-y-3 max-w-md shadow-xl">
              {character.description ? (
                <p className="text-sm text-slate-100 whitespace-pre-line leading-relaxed line-clamp-3">
                  {character.description}
                </p>
              ) : (
                <p className="text-sm text-slate-300 italic">（經營者尚未填寫角色介紹）</p>
              )}
              {acquireErr && (
                <div role="alert" className="text-xs text-rose-300 bg-rose-950/60 rounded p-2 break-words">{acquireErr}</div>
              )}
              <button
                type="button"
                onClick={() => { void doAcquire(); }}
                disabled={acquiring}
                className={`w-full sm:w-auto rounded-full px-6 py-3 text-white font-medium text-base shadow-lg disabled:opacity-50 ${
                  character.modelDistribution?.mode === 'paid'
                    ? 'bg-violet-500 hover:bg-violet-400'
                    : 'bg-blue-500 hover:bg-blue-400'
                }`}
              >
                {acquiring
                  ? '處理中…'
                  : character.modelDistribution?.mode === 'paid'
                    ? `用 ${character.modelDistribution.pricePoints} 黑耀石解鎖`
                    : '免費加入'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Insufficient-funds modal — opens when acquireCharacter returns
          402. Sends the fan to /wallet which has the ECPay package
          grid; we pass ?need=<short> so the wallet can highlight the
          smallest package that covers the gap. */}
      {insufficientFunds && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="insufficient-funds-title"
          onClick={() => setInsufficientFunds(null)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/80 backdrop-blur p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl space-y-4"
          >
            <h2 id="insufficient-funds-title" className="text-lg font-semibold text-slate-100">
              點數不足
            </h2>
            <div className="space-y-1 text-sm text-slate-300">
              <p>
                解鎖此角色需要 <span className="font-semibold text-violet-300">{insufficientFunds.required.toLocaleString()}</span> 點黑耀石。
              </p>
              <p>
                你目前餘額：<span className="font-semibold text-slate-100">{insufficientFunds.current.toLocaleString()}</span> 點
                {insufficientFunds.required > insufficientFunds.current && (
                  <span className="text-rose-300">
                    {' '}（還差 {(insufficientFunds.required - insufficientFunds.current).toLocaleString()} 點）
                  </span>
                )}
              </p>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              錢包頁有 ECPay 儲值方案，挑一個面額即可購買黑耀石。儲值完成後再回來解鎖角色。
            </p>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setInsufficientFunds(null)}
                className="flex-1 rounded-full border border-slate-700 bg-slate-800/50 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
              >稍後再說</button>
              <button
                type="button"
                onClick={() => {
                  const need = Math.max(0, insufficientFunds.required - insufficientFunds.current);
                  setInsufficientFunds(null);
                  navigate(`/wallet?need=${need}`);
                }}
                className="flex-1 rounded-full bg-violet-500 px-4 py-2 text-sm text-white hover:bg-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                autoFocus
              >立刻儲值</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox — full-screen image viewer. Renders outside the hero
          section so it stacks above everything. Click backdrop or ✕
          to close, Escape also works (handler hooked into window). */}
      {lightboxUrl && (
        <div
          role="dialog"
          aria-label="圖片預覽"
          aria-modal="true"
          onClick={() => setLightboxUrl(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur p-4"
        >
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 rounded-full bg-slate-900/80 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-500"
            aria-label="關閉"
          >✕</button>
          <img
            src={lightboxUrl}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full object-contain cursor-default"
          />
        </div>
      )}

      {/* ── Sidebar: info + function tiles ────────────────────────── */}
      <aside className="flex flex-col gap-3 lg:overflow-y-auto lg:max-h-full">
        {/* Compact info block — only shown after acquisition. The
            character name + operator are already rendered on the hero
            name chip; here we just surface the operator-authored
            description so the fan has context next to the chat CTA. */}
        {owned && character.description && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <p className="text-sm text-slate-300 whitespace-pre-line leading-relaxed line-clamp-4">
              {character.description}
            </p>
          </div>
        )}

        {/* Primary CTA — promoted out of the function grid when the
            fan owns the character, because chat IS the experience.
            Unowned fans see it as a disabled tile inside the grid
            below so the value-prop is still visible. */}
        {canEnterChat && (
          <button
            type="button"
            onClick={() => { if (!incident) navigate(`/c/${character.id}/chat`); }}
            disabled={!!incident}
            className={`rounded-2xl text-white shadow-lg px-5 py-4 flex items-center justify-between gap-3 focus:outline-none active:scale-[0.99] transition ${
              incident
                ? 'bg-slate-800 shadow-none cursor-not-allowed'
                : 'bg-gradient-to-r from-violet-500 to-violet-400 hover:from-violet-400 hover:to-violet-300 shadow-violet-500/20 focus:ring-2 focus:ring-violet-300'
            }`}
            aria-label={incident ? '維護中，暫無法聊天' : `與 ${character.name} 聊天`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-3xl shrink-0" aria-hidden="true">{incident ? '🛠' : '💬'}</span>
              <div className="text-left min-w-0">
                <div className="text-base font-semibold leading-tight">
                  {incident ? '維護中' : '開始聊天'}
                </div>
                <div className="text-xs text-violet-100/90 mt-0.5">
                  {incident
                    ? '此角色語音 / 對話服務暫停，稍後再試'
                    : `與 ${character.name} 對話 · 每則 ${character.pricing?.text ?? 1} 點`}
                </div>
              </div>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5 shrink-0"
              aria-hidden="true"
            >
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Function tile grid — secondary actions. When owned the
            primary chat CTA lives above; this grid is the "browse
            other capabilities" surface. Unowned: 開始聊天 stays in
            the grid (disabled with hint) so the value-prop reads. */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 sm:p-4">
          <h2 className="text-xs uppercase tracking-wider text-slate-500 mb-2 px-1">功能</h2>
          <div className="grid grid-cols-3 gap-2">
            {!canEnterChat && (
              <FeatureTile
                icon="💬"
                label="開始聊天"
                description={`每則 ${character.pricing?.text ?? 1} 點`}
                disabled
                disabledHint="加入後可用"
              />
            )}
            <FeatureTile
              icon="💰"
              label="錢包"
              description="儲值 · 轉點"
              onClick={() => navigate('/wallet')}
            />
            <FeatureTile
              icon="🛍️"
              label="物件庫"
              description="購買後通用"
              onClick={() => navigate('/objects')}
            />
            {/* Per `fan-scene-customization` — decorate scene editor.
                Owners only; unowned fans see this tile disabled with
                a hint pointing back at the acquire CTA. */}
            <FeatureTile
              icon="🪑"
              label="布置房間"
              description={canEnterChat ? '擺放擁有的物件' : '加入後可用'}
              onClick={() => navigate(`/c/${character.id}/decorate`)}
              disabled={!canEnterChat}
              disabledHint="加入後可用"
            />
            <FeatureTile
              icon="🧩"
              label="Chrome 擴充"
              description="桌寵套件"
              onClick={() => {
                const url = (import.meta.env.VITE_CHROME_WEBSTORE_URL as string | undefined)?.trim();
                if (url) window.open(url, '_blank', 'noopener');
              }}
              disabled={!(import.meta.env.VITE_CHROME_WEBSTORE_URL as string | undefined)?.trim()}
              disabledHint="準備上架"
            />
            {/* Per `fan-game-bridge` — one tile per registry game whose
                characterScope covers this character. Synced live from
                the 後台遊戲註冊表, no frontend deploy needed per game. */}
            {games.map((g) => (
              <FeatureTile
                key={g.gameId}
                icon={g.iconUrl ? undefined : '🎮'}
                iconUrl={g.iconUrl ?? undefined}
                label={g.name}
                description={g.description ?? '小遊戲'}
                onClick={() => void onLaunchGame(g.gameId)}
                disabled={!canEnterChat || launchingGameId === g.gameId}
                disabledHint={launchingGameId === g.gameId ? '啟動中…' : '加入後可用'}
              />
            ))}
            <FeatureTile icon="⋯" label="更多功能" description="開發中" disabled disabledHint="敬請期待" />
          </div>
        </div>

        {/* Owned-objects panel — shows up only after acquisition so
            the fan can apply purchased library items to this
            character. Hidden for unowned fans (their objects exist
            but they can't attach without owning the skin). */}
        <OwnedObjectsPanel
          characterId={character.id}
          characterName={character.name}
          ownedByFan={owned === true}
        />
      </aside>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// FeatureTile — one entry in the character's app-store grid.
// Visually mimics an iOS home-screen icon: large emoji on top, two
// lines of text below. Tapping the whole card is the affordance.
// ────────────────────────────────────────────────────────────────────
interface FeatureTileProps {
  icon?: string;
  /** fan-game-bridge — registry-supplied game icon; wins over emoji. */
  iconUrl?: string;
  label: string;
  description: string;
  onClick?: () => void;
  disabled?: boolean;
  disabledHint?: string;
  highlight?: boolean;
}
function FeatureTile({ icon, iconUrl, label, description, onClick, disabled, disabledHint, highlight }: FeatureTileProps) {
  const iconNode = iconUrl
    ? <img src={iconUrl} alt="" className="mb-0.5 h-8 w-8 rounded-lg object-cover sm:h-9 sm:w-9" />
    : null;
  const baseClasses =
    'aspect-square rounded-2xl border p-2 flex flex-col items-center justify-center text-center transition focus:outline-none focus:ring-2 focus:ring-violet-500';
  if (disabled) {
    return (
      <div
        aria-disabled="true"
        title={disabledHint}
        className={`${baseClasses} border-slate-800 bg-slate-900/40 opacity-60 cursor-not-allowed`}
      >
        {iconNode ?? <div className="text-2xl sm:text-3xl mb-0.5 grayscale">{icon}</div>}
        <div className="text-[12px] font-medium text-slate-300 leading-tight">{label}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{disabledHint ?? description}</div>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClasses} ${
        highlight
          ? 'border-violet-500/60 bg-violet-500/10 hover:bg-violet-500/20'
          : 'border-slate-800 bg-slate-900/60 hover:bg-slate-800/80 hover:border-slate-700'
      }`}
    >
      {iconNode ?? <div className="text-2xl sm:text-3xl mb-0.5">{icon}</div>}
      <div className="text-[12px] font-medium text-slate-100 leading-tight">{label}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{description}</div>
    </button>
  );
}
